import { Inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DeepPartial, mergeDeep } from '@apollo/client/utilities';
import { asyncScheduler } from 'rxjs';

import { ApolloCacheFactory, ApolloCacheManager } from '@shure/cloud/shared/apollo';
import { CloudFeatureFlagsService, FEATURE_FLAG_SERVICE } from '@shure/cloud/shared/utils/feature-flags';

import {
	InventoryDeviceByIdQueryDocument,
	InventoryDeviceByIdQueryOpResult,
	InventoryDeviceByIdQueryVariables,
	InventoryDeviceFragment
} from './graphql/generated/cloud-sys-api';

@Injectable({ providedIn: 'root' })
export class SysApiDeviceInventoryApolloCache {
	private readonly iotGatewayFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-iot-gateway'), {
		initialValue: false
	});
	private readonly extendedWarrantyFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-extended-warranty'),
		{
			initialValue: false
		}
	);
	private readonly scheduledJobsFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-scheduled-jobs'),
		{
			initialValue: false
		}
	);
	private readonly iotRoomProxyFeatureEnabled = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-iot-room-proxy'),
		{
			initialValue: false
		}
	);
	private readonly auracastFeatureEnabled = toSignal(this.featureFlagsService.getFeatureFlag$('cdm-auracast'), {
		initialValue: false
	});

	private readonly useFirmwareUpdateV2 = toSignal(
		this.featureFlagsService.getFeatureFlag$('cdm-use-firmware-update-v2'),
		{
			initialValue: false
		}
	);

	constructor(
		private readonly apolloCacheManager: ApolloCacheManager,
		private readonly apolloCache: ApolloCacheFactory,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagsService: CloudFeatureFlagsService
	) {}

	public seedEntry(device: InventoryDeviceFragment): void {
		const variables: InventoryDeviceByIdQueryVariables = {
			nodeId: device.id,
			requestAuthenticationProperties: this.iotGatewayFeatureEnabled(),
			requestCloudGatewayProperties: this.iotGatewayFeatureEnabled(),
			requestExtendedWarrantyProperties: this.extendedWarrantyFeatureEnabled(),
			requestScheduledJobs: this.scheduledJobsFeatureEnabled(),
			requestIotRoomProxy: this.iotRoomProxyFeatureEnabled(),
			requestAuracastProperties: this.auracastFeatureEnabled(),
			useFirmwareUpdateV2: this.useFirmwareUpdateV2()
		};
		this.apolloCache.cache?.writeQuery({
			query: InventoryDeviceByIdQueryDocument,
			data: { node: device },
			variables,
			broadcast: false, // significant perf cost if this is TRUE.
			overwrite: true
		});
	}

	public removeEntry(id: string): void {
		// Schedule the cache removal for a short amount of time in the future
		// This allows any watchQueries to get removed w/out reporting errors.
		asyncScheduler.schedule(
			() => this.apolloCacheManager.getApolloCache('sysApi')?.evict({ id: `Device:${id}`, broadcast: false }),
			1000
		);
	}

	public updateFeature<TFeatureKey extends keyof InventoryDeviceFragment['features']>(
		id: string,
		featureKey: TFeatureKey,
		newData: DeepPartial<InventoryDeviceFragment['features'][TFeatureKey]>
	): void {
		const queryVars: InventoryDeviceByIdQueryVariables = {
			nodeId: id,
			requestAuthenticationProperties: this.iotGatewayFeatureEnabled(),
			requestCloudGatewayProperties: this.iotGatewayFeatureEnabled(),
			requestExtendedWarrantyProperties: this.extendedWarrantyFeatureEnabled(),
			requestScheduledJobs: this.scheduledJobsFeatureEnabled(),
			requestIotRoomProxy: this.iotRoomProxyFeatureEnabled(),
			requestAuracastProperties: this.auracastFeatureEnabled(),
			useFirmwareUpdateV2: this.useFirmwareUpdateV2()
		};

		const cachedData = this.apolloCache.cache?.readQuery<
			InventoryDeviceByIdQueryOpResult,
			InventoryDeviceByIdQueryVariables
		>({
			query: InventoryDeviceByIdQueryDocument,
			variables: queryVars
		});

		const cachedDevice = <InventoryDeviceFragment>cachedData?.node;
		if (!cachedDevice) {
			return;
		}

		this.apolloCache.cache?.writeQuery<InventoryDeviceByIdQueryOpResult, InventoryDeviceByIdQueryVariables>({
			query: InventoryDeviceByIdQueryDocument,
			variables: queryVars,
			data: {
				node: mergeDeep(cachedDevice, {
					features: {
						// eslint-disable-next-line @typescript-eslint/naming-convention
						__typename: 'DeviceFeatures',
						[featureKey]: newData
					}
				})
			}
		});
	}
}
